'use client';

import { useEffect, useState, useRef, useTransition } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import LoadingOverlay from './LoadingOverlay';

export default function RouteChangeLoader() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const prevPathRef = useRef<string>('');
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MINIMUM_LOADING_TIME = 400; // Minimum time to show loader (ms)
  const MAX_LOADING_TIME = 5000; // Maximum time before forcing hide (ms)

  // Listen for clicks on all links (both <a> and <Link>)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Ignore clicks on dropdowns, buttons, and form elements
      if (
        target.closest('.dropdown') ||
        target.closest('.dropdown-menu') ||
        target.closest('button') ||
        target.closest('[role="button"]') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea') ||
        target.closest('[data-no-loader]') || // Allow custom attribute to skip loader
        target.closest('.mega-menu') || // Ignore clicks inside mega menus (they're just toggles)
        target.closest('.web-list') // Ignore clicks in website dropdown
      ) {
        return;
      }
      
      const link = target.closest('a[href]');
      
      if (link) {
        const href = link.getAttribute('href');
        
        // Skip hash links, external links, and special attributes
        if (
          !href ||
          href === '#' ||
          href.startsWith('#') ||
          href.startsWith('javascript:') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          link.hasAttribute('target') ||
          link.hasAttribute('download') ||
          link.closest('.mega-menu') || // Links inside mega menus are toggles
          link.closest('.web-list') // Links in website dropdown
        ) {
          return;
        }
        
        try {
          const url = href.startsWith('http') 
            ? new URL(href) 
            : new URL(href, window.location.origin);
          
          const currentUrl = new URL(window.location.href);
          
          if (url.origin === currentUrl.origin) {
            const newPath = url.pathname + url.search;
            const currentPath = currentUrl.pathname + currentUrl.search;
            
            if (newPath !== currentPath) {
              console.log('🔗 Link clicked - Showing loader');
              setIsLoading(true);
              
              // Safety timeout: hide loader after MAX_LOADING_TIME if route doesn't change
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
              }
              loadingTimeoutRef.current = setTimeout(() => {
                console.log('⚠️ Max loading time reached, forcing hide');
                setIsLoading(false);
              }, MAX_LOADING_TIME);
            } else {
              // Same route clicked - don't show loader at all
              console.log('⚠️ Same route clicked, ignoring');
            }
          }
        } catch (error) {
          console.error('Error parsing URL:', error);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Handle route change completion
  useEffect(() => {
    const currentPath = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    
    if (prevPathRef.current && currentPath !== prevPathRef.current) {
      console.log('✅ Route changed to:', currentPath);
      
      // Clear the safety timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Keep loader visible for minimum time
      setTimeout(() => {
        console.log('✨ Hiding loader');
        setIsLoading(false);
      }, MINIMUM_LOADING_TIME);
    } else if (!prevPathRef.current) {
      // First render
      prevPathRef.current = currentPath;
    }
    
    prevPathRef.current = currentPath;
  }, [pathname, searchParams]);

  return <LoadingOverlay isLoading={isLoading} message="" />;
}

